/* Journal topic rail loader.
 *
 * Each <journal-rail> holds four skeleton cards server-rendered by
 * sections/journal-rail.liquid. When the rail nears the viewport it fetches
 * data-src (the blog's tagged route with ?view=rail, rendered by
 * templates/blog.rail.liquid without a layout) and swaps the real cards in.
 * The fragment and the skeletons share the same .fbjm-card grid footprint, so
 * the swap does not shift layout. Any failure, or a tag with no articles yet,
 * degrades to a plain link to the tagged route.
 *
 * A rail never repeats the Journal's lead story. The fragment returns one card
 * more than the rail shows, so dropping the lead still leaves four.
 */
(function () {
  'use strict';

  if (customElements.get('journal-rail')) return;

  const RAIL_SIZE = 4;

  function pathOf(href) {
    try {
      return new URL(href, window.location.href).pathname.replace(/\/+$/, '');
    } catch (error) {
      return '';
    }
  }

  function leadPaths() {
    const paths = new Set();
    document.querySelectorAll('.fbjm-lead a[href]').forEach((link) => {
      paths.add(pathOf(link.getAttribute('href')));
    });
    return paths;
  }

  function pickCards(cards, skip) {
    return Array.from(cards)
      .filter((card) => {
        const link = card.querySelector('a[href]');
        return !link || !skip.has(pathOf(link.getAttribute('href')));
      })
      .slice(0, RAIL_SIZE);
  }

  class JournalRail extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      if (!('IntersectionObserver' in window)) {
        this._load();
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this._observer.disconnect();
            this._load();
          }
        },
        { rootMargin: '600px 0px' }
      );
      this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    async _load() {
      const grid = this.querySelector('[data-rail-cards]');
      const src = this.dataset.src;
      if (!grid || !src) return;

      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error('rail fetch ' + response.status);
        const markup = await response.text();
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const cards = pickCards(
          doc.querySelectorAll('[data-journal-rail-fragment] .fbjm-card'),
          leadPaths()
        );
        if (!cards.length) {
          this._fallback(grid);
          return;
        }
        grid.replaceChildren(...cards);
        grid.removeAttribute('aria-busy');
      } catch (error) {
        this._fallback(grid);
      }
    }

    _fallback(grid) {
      const link = document.createElement('a');
      link.className = 'fbjm-go fbjm-rail__fallback';
      link.href = this.dataset.fallbackHref || this.dataset.src.split('?')[0];
      link.textContent = this.dataset.fallbackLabel || 'View all';
      grid.replaceChildren(link);
      grid.removeAttribute('aria-busy');
    }
  }

  customElements.define('journal-rail', JournalRail);
})();
